<template>
  <v-container>
    <v-row>
      <v-col>
        <h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon
                     @click="$router.push({name: 'campaign', params: {campaignId: party.campaign.id}})"
                     v-on="on"
              >
                <v-icon x-large>mdi-chevron-left</v-icon>
              </v-btn>
            </template>
            <span>Go Back</span>
          </v-tooltip>
          Party - {{ party.name }}
        </h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="6">
        <v-card>
          <v-card-title>Party Information</v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" lg="6">
                <div class="subtitle-2">General Information</div>
                <div>
                  Reputation: {{ party.reputation }}

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon
                             color="error"
                             @click="lowerPartyReputation(party)"
                             v-on="on"
                      >
                        <v-icon>mdi-minus-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Decrease Reputation</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon
                             color="primary"
                             @click="raisePartyReputation(party)"
                             v-on="on"
                      >
                        <v-icon>mdi-plus-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Increase Reputation</span>
                  </v-tooltip>
                </div>
              </v-col>

              <v-col cols="12" lg="6">
                <div class="subtitle-2">Shop Information</div>
                <div>Shop Price Modifier: {{ getShopPriceModifierString(party.shopPriceModifier) }}
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-textarea v-model="notes"
                            outlined
                            auto-grow
                            @input="updateNotesDelay($route.params.partyId)"
                            label="Notes"/>

                <v-chip color="warning" v-if="notesSaving">
                  <v-progress-circular indeterminate size="18" width="2" class="mr-4"/>
                  Saving...
                </v-chip>
                <v-chip color="success" v-else>
                  <v-icon class="mr-2">mdi-check</v-icon>
                  Saved
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider/>

          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon
                       color="error"
                       @click="destroyParty(party)"
                       v-on="on"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Party</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>

        <v-card class="mt-6">
          <v-card-title>Characters</v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" v-for="(character, characterIndex) in party.characters"
                     :key="characterIndex">
                <div class="title">
                  <v-avatar size="32" class="mb-2 mr-2">
                    <v-img :src="getClassAvatar(character.character_class)"/>
                  </v-avatar>
                  {{ character.name }}
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon
                             color="primary"
                             @click="$router.push({name: 'character', params: {characterId: character.id}})"
                             v-on="on">
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>View Character</span>
                  </v-tooltip>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider/>

          <v-card-actions>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon
                       color="primary"
                       @click="$router.push({name: 'create-character', params: {partyId: $route.params.partyId}})"
                       v-on="on"
                >
                  <v-icon>mdi-account-plus</v-icon>
                </v-btn>
              </template>
              <span>Create Character</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>

        <v-card class="mt-6">
          <v-card-title>Achievements</v-card-title>

          <v-card-text>
              <div v-for="(achievement, achievementIndex) in party.achievements" :key="achievementIndex">
                <span>
                  {{ achievement.title }}
                  <span v-if="achievement.levels > 1"> - Level {{ achievement.level }}</span>
                </span>
              </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card>
          <v-card-title>Scenarios</v-card-title>

          <v-card-text>
            <div v-for="(scenario, scenarioIndex) in scenarios" :key="scenarioIndex">
              <ScenarioListItem :scenario="scenario"
                                :scenario-index="scenarioIndex"
                                :party="party"/>
            </div>
          </v-card-text>

        </v-card>

        <v-card class="mt-6">
          <v-card-title>Available Items</v-card-title>

          <v-card-text>
            <div v-for="(item, itemIndex) in party.campaign.items" :key="itemIndex">
              {{ item.amount }} pcs of #{{ item.number }} - {{ item.slot }} - {{ item.title }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import PartyService from "../services/PartyService";
import CampaignService from "../services/CampaignService";
import ScenarioService from "../services/ScenarioService";
import ScenarioListItem from "@/components/ScenarioListItem";

export default {
  components: {ScenarioListItem},
  data: () => ({
    party: {
      id: Number,
      campaign: {
        id: Number
      }
    },
    notesSaving: false,
    notesTimeout: null,
    notes: null,
    scenarios: [
      {}
    ]
  }),

  created() {
    this.viewParty();
    this.indexScenarios();

  },

  methods: {
    viewParty() {
      PartyService.view(this.$route.params.partyId)
          .then(response => {
            this.party = response.data.data;
            this.notes = this.party.notes;
          })
          .catch(err => {
            console.log(err);
          });
    },

    indexScenarios() {
      ScenarioService.indexByParty(this.$route.params.partyId)
          .then(response => {
            this.scenarios = response.data.data;
          })
          .catch(err => {
            console.log(err);
          });
    },

    raisePartyReputation(party) {
      PartyService.raiseReputation(party.id)
          .then(() => {
            this.viewParty();
          })
          .catch(err => {
            console.log(err);
          });
    },

    lowerPartyReputation(party) {
      PartyService.lowerReputation(party.id)
          .then(() => {
            this.viewParty();
          })
          .catch(err => {
            console.log(err);
          });
    },

    destroyParty(party) {
      if (confirm(`Are you sure you want to remove the party ${party.name}?`)) {
        PartyService.destroy(party.id)
            .then(() => {
              this.$router.push({name: 'campaign', params: {campaignId: this.party.campaign.id}});
            })
            .catch(err => {
              console.log(err);
            });
      }
    },

    getShopPriceModifierString(shopPriceModifier) {
      if (shopPriceModifier > 0) {
        return `+${shopPriceModifier}`;
      }

      return shopPriceModifier;
    },

    unlockItemsByProsperity() {
      CampaignService.unlockItemsByProsperity(this.party.campaign.id)
          .then(() => {
            this.viewParty();
          })
          .catch(err => {
            console.log(err);
          });
    },

    updateNotesDelay(partyId) {
      this.notesSaving = true;

      if (this.notesTimeout) {
        clearTimeout(this.notesTimeout);
        this.notesTimeout = null;
      }

      this.notesTimeout = setTimeout(() => {
        this.updateNotes(partyId);
      }, 3000);
    },

    updateNotes(partyId) {
      PartyService.updateNotes(partyId, {
        notes: this.notes
      })
          .then(() => {
            this.notesSaving = false;
          })
          .catch(err => {
            console.log(err);
            this.notesSaving = false;
          });
    },

    getClassAvatar(characterClass) {
      try {
        return require(`@/assets/${characterClass.slug}.png`);
      } catch (e) {
        return "";
      }
    }
  }
};
</script>